<template>
  <el-upload
    ref="uploadRef"
    v-bind="$attrs"
    class="app-upload"
    :class="{ 'app-upload--card': isCard, 'app-upload--busy': busy }"
    :limit="limit"
    :disabled="busy || $attrs.disabled === true"
    :on-change="handleChange"
    :on-exceed="handleExceed"
    :aria-busy="busy"
  >
    <template #default>
      <slot />
      <span v-if="busy" class="app-upload-status" role="status">
        <el-icon class="is-loading"><Loading /></el-icon>
        正在处理并上传…
      </span>
    </template>
    <template v-for="(_, name) in namedSlots" :key="name" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps || {}" />
    </template>
  </el-upload>
</template>

<script>
export default { inheritAttrs: false }
</script>

<script setup>
import { computed, nextTick, ref, useAttrs, useSlots } from 'vue'
import { ElMessage, genFileId } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'

const props = defineProps({
  limit: { type: Number, default: undefined },
  onChange: { type: Function, default: undefined },
  onExceed: { type: Function, default: undefined }
})
const attrs = useAttrs()
const slots = useSlots()
const namedSlots = computed(() => Object.fromEntries(Object.entries(slots).filter(([name]) => name !== 'default')))
const isCard = computed(() => attrs.drag === '' || attrs.drag === true || attrs['list-type'] === 'picture-card' || attrs.listType === 'picture-card')
const uploadRef = ref(null)
const pending = ref(0)
const busy = computed(() => pending.value > 0)

const handleChange = async (file, files) => {
  if (!props.onChange) return
  if (!file.raw || file.status !== 'ready') return props.onChange(file, files)
  pending.value++
  try {
    // 只在选中文件后让出渲染时间；打开本地文件窗口不做异步工作。
    await nextTick()
    await new Promise(resolve => requestAnimationFrame(resolve))
    await props.onChange(file, files)
  } finally {
    pending.value--
  }
}

const handleExceed = (files, uploadFiles) => {
  if (props.onExceed) return props.onExceed(files, uploadFiles)
  if (props.limit === 1 && files.length === 1) {
    uploadRef.value.clearFiles()
    files[0].uid = genFileId()
    uploadRef.value.handleStart(files[0])
  } else {
    ElMessage.warning(`最多可上传 ${props.limit} 个文件，请先移除已有文件`)
  }
}

defineExpose({
  clearFiles: (...args) => uploadRef.value?.clearFiles(...args),
  handleStart: (...args) => uploadRef.value?.handleStart(...args),
  handleRemove: (...args) => uploadRef.value?.handleRemove(...args),
  abort: (...args) => uploadRef.value?.abort(...args),
  submit: (...args) => uploadRef.value?.submit(...args)
})
</script>

<style lang="scss">
.app-upload {
  max-width: 100%;
  .el-upload { position: relative; max-width: 100%; }
  .el-upload__tip { margin-top: 8px; color: #8d9094; font-size: 12px; line-height: 20px; }
  .app-upload-status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    font-size: 13px;
    color: #0c7ae1;
    pointer-events: none;
  }
  &.app-upload--card {
    .el-upload--picture-card, .el-upload-dragger {
      width: 200px !important;
      height: 200px !important;
      max-width: 100%;
      padding: 20px !important;
      box-sizing: border-box;
      border: 1px dashed #1890ff !important;
      border-radius: 8px !important;
      background: #fff !important;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
      position: relative;
      &:hover, &:focus-within { background-color: #f5faff !important; }
    }
    .el-upload__text, .consultation-upload-text {
      font-size: 14px;
      line-height: 20px;
      color: #606266 !important;
    }
    .el-icon--upload, .consultation-upload-icon { font-size: 64px !important; color: #a8abb2; margin: 0 !important; }
    .uploaded-image {
      position: absolute !important;
      inset: 0;
      width: 100% !important;
      height: 100% !important;
      object-fit: contain;
      background-color: #fff;
      border-radius: 8px;
      pointer-events: none;
    }
    .el-upload-dragger:has(.uploaded-image) {
      .el-icon--upload, .el-upload__text { visibility: hidden; }
    }
    .app-upload-status { position: absolute; inset: 0; z-index: 10; background: rgba(255,255,255,.88); }
  }
  &.app-upload--busy .el-upload { cursor: wait; }
}
@media (max-width: 480px) {
  .id-card-upload { flex-wrap: wrap; }
}
</style>
